import type { TInteraction } from "@/schemas/contacts"
import type { TPhoneStatus } from "@/schemas/user.schema"

export type TSequenceCallSimulationUser = "roberto" | "meliza" | "ashish" | "me"
export type TSequenceCallSimulationStatus = Extract<TPhoneStatus, "AVAILABLE" | "RINGING" | "TALKING">
export type TSequenceCallSimulationPreset = "first-login" | "wave" | "clear"
export type TSequenceCallSimulationTargetAlias = "first" | "second" | "third"

export interface TSequenceDialerRowLike {
  id?: string | number | null
  ContactPersonId?: string | null
}

export type TSequenceCallSimulationTarget = { type: "alias"; value: TSequenceCallSimulationTargetAlias } | { type: "contactId"; value: string }

export type TSequenceCallSimulationCommand =
  | {
      type: "preset"
      preset: TSequenceCallSimulationPreset
    }
  | {
      type: "freeform"
      user: TSequenceCallSimulationUser
      status: TSequenceCallSimulationStatus
      target: TSequenceCallSimulationTarget
    }

export interface TSequenceCallSimulationDesiredAssignment {
  user: TSequenceCallSimulationUser
  status: Exclude<TSequenceCallSimulationStatus, "AVAILABLE">
  contactId: string
}

export interface TSequenceCallSimulationLiveCallLike {
  contactId: string
  ringId: string | null | undefined
  status: TPhoneStatus
  direction: TInteraction["Direction"]
}

export const DEV_SEQUENCE_SIM_USER_NAMES: Record<TSequenceCallSimulationUser, string> = {
  roberto: "Roberto",
  meliza: "Meliza",
  ashish: "Ashish",
  me: "Me",
}

const TARGET_ALIAS_INDEX: Record<TSequenceCallSimulationTargetAlias, number> = {
  first: 0,
  second: 1,
  third: 2,
}

function normalizeWhitespace(value: string) {
  return value.trim().toLowerCase().replace(/\s+/g, " ")
}

function parseStatus(input: string): TSequenceCallSimulationStatus | null {
  if (input === "ringing") return "RINGING"
  if (input === "talking") return "TALKING"
  if (input === "available") return "AVAILABLE"
  return null
}

function parseUser(input: string): TSequenceCallSimulationUser | null {
  if (input === "roberto") return "roberto"
  if (input === "meliza") return "meliza"
  if (input === "ashish") return "ashish"
  if (input === "me") return "me"
  return null
}

function parseTarget(input: string): TSequenceCallSimulationTarget | null {
  if (input === "first" || input === "second" || input === "third") {
    return { type: "alias", value: input }
  }

  if (/^\d+$/.test(input)) {
    return { type: "contactId", value: input }
  }

  return null
}

function parsePreset(input: string): TSequenceCallSimulationPreset | null {
  if (input === "first-login") return "first-login"
  if (input === "wave") return "wave"
  if (input === "clear") return "clear"
  return null
}

export function parseSequenceCallSimulationCommand(rawQuery: string): TSequenceCallSimulationCommand | null {
  const normalized = normalizeWhitespace(rawQuery)
  if (!normalized) return null

  const tokens = normalized.split(" ")
  if (tokens.length < 3) return null

  const prefix = tokens[0]
  if (prefix !== "/seq" && prefix !== "seq") return null
  if (tokens[1] !== "sim") return null

  if (tokens.length === 3) {
    const preset = parsePreset(tokens[2])
    if (!preset) return null

    return {
      type: "preset",
      preset,
    }
  }

  if (tokens.length !== 5) return null

  const user = parseUser(tokens[2])
  const status = parseStatus(tokens[3])
  const target = parseTarget(tokens[4])

  if (!user || !status || !target) return null

  return {
    type: "freeform",
    user,
    status,
    target,
  }
}

function toContactId(row: TSequenceDialerRowLike | undefined): string | null {
  if (!row) return null
  if (row.id !== undefined && row.id !== null && `${row.id}`.length > 0) return `${row.id}`
  if (row.ContactPersonId && `${row.ContactPersonId}`.length > 0) return `${row.ContactPersonId}`
  return null
}

export function resolveSimulationTargetToContactId(target: TSequenceCallSimulationTarget, rows: TSequenceDialerRowLike[]): string | null {
  if (target.type === "contactId") return target.value

  const targetRow = rows[TARGET_ALIAS_INDEX[target.value]]
  return toContactId(targetRow)
}

export function buildPresetDesiredAssignments(preset: TSequenceCallSimulationPreset, rows: TSequenceDialerRowLike[]): TSequenceCallSimulationDesiredAssignment[] {
  if (preset === "clear") return []

  const first = toContactId(rows[0])
  const second = toContactId(rows[1])
  const third = toContactId(rows[2])

  const assignments: TSequenceCallSimulationDesiredAssignment[] = []

  if (preset === "first-login") {
    if (first) assignments.push({ user: "roberto", status: "TALKING", contactId: first })
    if (second) assignments.push({ user: "meliza", status: "TALKING", contactId: second })
    if (third) assignments.push({ user: "me", status: "TALKING", contactId: third })
    return assignments
  }

  if (first) assignments.push({ user: "roberto", status: "RINGING", contactId: first })
  if (second) assignments.push({ user: "meliza", status: "TALKING", contactId: second })
  if (third) assignments.push({ user: "ashish", status: "TALKING", contactId: third })

  return assignments
}

export function isSequenceSimulationDevEnabled() {
  return process.env.NEXT_PUBLIC_ENV !== "production"
}

export function shouldShowSequenceSimulationDevActions(query: string) {
  if (!isSequenceSimulationDevEnabled()) return false

  const normalized = normalizeWhitespace(query)
  if (!normalized) return false

  if (normalized === "/seq" || normalized.startsWith("/seq")) return true
  if (normalized.startsWith("seq sim")) return true
  if (normalized.includes("sequence sim")) return true

  return false
}

export function buildEffectiveLiveCalls(realLiveCalls: TSequenceCallSimulationLiveCallLike[], overlayLiveCalls: TSequenceCallSimulationLiveCallLike[]) {
  const map = new Map<string, TSequenceCallSimulationLiveCallLike>()

  for (const overlayCall of overlayLiveCalls) {
    map.set(`${overlayCall.contactId}`, overlayCall)
  }

  for (const realCall of realLiveCalls) {
    if (!map.has(`${realCall.contactId}`)) {
      map.set(`${realCall.contactId}`, realCall)
    }
  }

  return [...map.values()]
}
